import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { api, type Library, type MusicArchive } from '../api.ts';
import { AlbumCard, ArtistCard } from '../components/MusicCards.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import { MusicIcon, PlusIcon } from '../Icon.tsx';

function musicLibraryOf(libraries: Library[] | null): Library | undefined {
  return libraries?.find(
    (library) =>
      library.kind === 'music' ||
      library.name.toLowerCase().includes('müzik') ||
      library.name.toLowerCase().includes('muzik') ||
      library.name.toLowerCase().includes('music'),
  );
}

export function MusicScreen({
  libraries,
  onOpenLibrary,
  onOpenAlbum,
  onOpenArtist,
  onBack,
  onAddLibrary,
}: {
  libraries: Library[] | null;
  onOpenLibrary: (libraryId: string) => void;
  onOpenAlbum: (albumId: string) => void;
  onOpenArtist: (artistId: string) => void;
  onBack: () => void;
  onAddLibrary?: (() => void) | undefined;
}): ReactNode {
  const musicLib = musicLibraryOf(libraries);
  const [archive, setArchive] = useState<MusicArchive | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .music()
      .then((result) => {
        if (!cancelled) setArchive(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled)
          setError(caught instanceof Error ? caught.message : 'Müzik arşivi okunamadı.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error !== null) return <ErrorNotice message={error} />;
  if (musicLib === undefined) {
    return (
      <div className="music-screen">
        <div className="music-screen__hero">
          <div className="music-screen__icon-wrap" aria-hidden="true">
            <span className="music-screen__icon">
              <MusicIcon size={38} />
            </span>
          </div>
          <h1 className="music-screen__title">Müzik Koleksiyonu</h1>
          <p className="music-screen__subtitle">
            Albüm ve sanatçı arşivinizi yüksek kalitede, doğrudan tarayıcınızda dinleyin.
          </p>
          <div className="music-screen__empty-card">
            <h3>Henüz Müzik Kütüphanesi Eklenmedi</h3>
            <p>
              Sunucunuzdaki müzik dosyalarını (FLAC, MP3, AAC, OGG) oynatmak için bir müzik dizini
              bağlayın (ör. <code>/srv/media/muzikler</code>).
            </p>
            <div className="music-screen__formats">
              <span className="badge">FLAC Lossless</span>
              <span className="badge">MP3 320k</span>
              <span className="badge">AAC / M4A</span>
              <span className="badge">OGG Opus</span>
            </div>
            <div className="music-screen__buttons">
              {onAddLibrary && (
                <button type="button" className="button button--primary" onClick={onAddLibrary}>
                  <PlusIcon size={16} /> Müzik Kütüphanesi Ekle
                </button>
              )}
              <button type="button" className="button button--ghost" onClick={onBack}>
                Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (archive === null) return <Loading label="Müzik arşivi yükleniyor…" />;
  const hasContent = archive.albums.length > 0 || archive.artists.length > 0;

  return (
    <div className="music-page">
      <header className="music-page__header">
        <div className="music-page__heading">
          <span className="music-page__icon" aria-hidden="true">
            <MusicIcon size={28} />
          </span>
          <div>
            <h1>Müzik</h1>
            <p>{musicLib.name} · Albüm ve sanatçı arşivi</p>
          </div>
        </div>
        <div className="music-page__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => onOpenLibrary(musicLib.id)}
          >
            Kütüphaneyi aç
          </button>
        </div>
      </header>

      {!hasContent ? (
        <div className="music-page__empty">
          <h2>Henüz etiketlenmiş parça yok</h2>
          <p>
            Dosyalar tarandı ancak albüm/sanatçı etiketi bulunamadı. Kütüphaneyi tarayarak
            etiketleri yenileyebilirsiniz; etiketsiz parçalar “Bilinmeyen albüm/sanatçı” altında
            görünür.
          </p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => onOpenLibrary(musicLib.id)}
          >
            Kütüphaneyi aç
          </button>
        </div>
      ) : (
        <>
          {archive.albums.length > 0 && (
            <section className="music-section" aria-labelledby="music-albums-title">
              <div className="section__header">
                <h2 id="music-albums-title" className="section__title">
                  Albümler
                </h2>
                <span className="section__badge">{archive.albums.length} albüm</span>
              </div>
              <div className="music-album-grid">
                {archive.albums.map((album) => (
                  <AlbumCard key={album.id} album={album} onOpen={() => onOpenAlbum(album.id)} />
                ))}
              </div>
            </section>
          )}

          {archive.artists.length > 0 && (
            <section className="music-section" aria-labelledby="music-artists-title">
              <div className="section__header">
                <h2 id="music-artists-title" className="section__title">
                  Sanatçılar
                </h2>
                <span className="section__badge">{archive.artists.length} sanatçı</span>
              </div>
              <div className="music-artist-grid">
                {archive.artists.map((artist) => (
                  <ArtistCard
                    key={artist.id}
                    artist={artist}
                    onOpen={() => onOpenArtist(artist.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
